import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Image, Smile, Paperclip, Send, X, Check, ChevronDown, ChevronUp, File, ClipboardPaste, Trash2, Edit3, Loader2, MessageSquare, UserPlus, UserMinus, Search, RefreshCw, ArrowLeft } from 'lucide-react';
import { check, tm1, tm2, userimg, video } from '../../../assets/assets';
import { BsSendFill } from 'react-icons/bs';
import { getChatMessages, sendMessage, markMessagesAsRead } from '../../../services/message.service';
import { getChatById, createDirectChat, initiateCall, addParticipantToChat, removeParticipantFromChat } from '../../../services/chat.service';
import { AxiosInstance } from '../../../services/auth/AxiosInstance';
import { useAuthStore } from '../../../store/authStore';
import { useAlert } from '../../../context/AlertContext';
import SocketService from '../../../services/socket.service';

const emojiCategories = {
  'Smileys': ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥'],
  'Hearts': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟'],
  'Gestures': ['👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋', '🤝', '👏', '🙌', '👐', '🤲', '🤜', '🤛', '✊', '👊', '👶', '👦', '👧'],
  'Animals': ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦', '🐤', '🐣', '🐥', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟']
};

const EmojiPicker = ({ onEmojiSelect, onClose, isOpen }) => {
  const [activeCategory, setActiveCategory] = useState('Smileys');

  if (!isOpen) return null;

  return (
    <div className="absolute bottom-16 left-0 bg-white dark:bg-[#1d2026] border dark:border-[#32353c] rounded-lg shadow-lg w-80 h-64 z-50">
      <div className="flex border-b dark:border-[#32353c]">
        {Object.keys(emojiCategories).map(category => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-3 py-2 text-xs font-medium ${
              activeCategory === category 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {category}
          </button>
        ))}
      </div>
      <div className="p-2 h-48 overflow-y-auto">
        <div className="grid grid-cols-8 gap-1">
          {emojiCategories[activeCategory].map((emoji, index) => (
            <button
              key={index}
              onClick={() => onEmojiSelect(emoji)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-lg"
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const MessageActionsDropdown = ({ message, onEdit, onDelete, onClose, isOpen }) => {
  const dropdownRef = useRef(null);
  
  // Check if message can be edited (within 15 minutes)
  const canEdit = () => {
    if (message.sender !== 'user') return false;
    const messageTime = new Date(message.timestamp);
    const now = new Date();
    const diffInMinutes = (now - messageTime) / (1000 * 60);
    return diffInMinutes <= 15;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      ref={dropdownRef}
      className="absolute right-0 top-8 bg-white dark:bg-[#1d2026] border dark:border-[#32353c] rounded-lg shadow-lg z-50 py-1 min-w-[120px]"
    >
      {canEdit() && (
        <button
          onClick={() => {
            onEdit(message);
            onClose();
          }}
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-300 flex items-center gap-2"
        >
          <Edit3 size={14} />
          Edit
        </button>
      )}
      <button
        onClick={() => {
          onDelete(message.id);
          onClose();
        }}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center gap-2"
      >
        <Trash2 size={14} />
        Delete
      </button>
    </div>
  );
};

const FilePreviewModal = ({ files, onClose, onRemoveFile, onSend, message, setMessage, type = 'image' }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  if (!files || files.length === 0) return null;

  const handleSend = () => {
    onSend(files, message);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-[#1d2026] rounded-lg max-w-4xl max-h-[90vh] w-full mx-4 flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b dark:border-[#32353c]">
          <h3 className="text-lg font-semibold dark:text-white">
            {type === 'image' ? 'Send Images' : 'Send Documents'} ({files.length})
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        {/* Main Preview Area */}
        <div className="flex-1 flex flex-col">
          {/* Large Preview */}
          <div className="flex-1 flex items-center justify-center p-4 bg-gray-50 dark:bg-[#191c22]">
            {type === 'image' ? (
              <img 
                src={files[selectedIndex]?.preview} 
                alt={`Preview ${selectedIndex + 1}`}
                className="max-w-full max-h-96 object-contain rounded"
              />
            ) : (
              <div className="flex flex-col items-center p-8 bg-white dark:bg-[#1d2026] rounded-lg border-2 border-dashed border-gray-300">
                <File size={48} className="text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-700">{files[selectedIndex]?.name}</p>
                <p className="text-sm text-gray-500">{files[selectedIndex]?.size}</p>
                <p className="text-xs text-gray-400 mt-2">{files[selectedIndex]?.type}</p>
              </div>
            )}
          </div>

          {/* Thumbnail Strip */}
          {files.length > 1 && (
            <div className="flex gap-2 p-4 border-t dark:border-[#32353c] bg-white dark:bg-[#1d2026] overflow-x-auto">
              {files.map((file, index) => (
                <div key={index} className="relative flex-shrink-0">
                  <button
                    onClick={() => setSelectedIndex(index)}
                    className={`relative block ${
                      selectedIndex === index ? 'ring-2 ring-blue-500' : ''
                    }`}
                  >
                    {type === 'image' ? (
                      <img 
                        src={file.preview} 
                        alt={`Thumbnail ${index + 1}`}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                        <File size={20} className="text-gray-500" />
                      </div>
                    )}
                  </button>
                  <button
                    onClick={() => onRemoveFile(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Single file remove button */}
          {files.length === 1 && (
            <div className="flex justify-center p-2 border-t dark:border-[#32353c] bg-white dark:bg-[#1d2026]">
              <button
                onClick={() => onRemoveFile(0)}
                className="flex items-center gap-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded-md"
              >
                <Trash2 size={16} />
                Remove {type}
              </button>
            </div>
          )}
        </div>

        {/* Message Input and Send */}
        <div className="border-t dark:border-[#32353c] p-4 bg-white dark:bg-[#1d2026]">
          <div className="flex gap-3">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`Add a caption for ${files.length} ${type}${files.length > 1 ? 's' : ''}...`}
              className="flex-1 p-1.5 border border-gray-300 text-gray-500 dark:text-gray-300 bg-white dark:bg-[#1d2026] dark:border-[#32353c] text-[13px] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSend}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Send size={18} />
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ImageCollage = ({ images, onImageClick }) => {
  const imageCount = images.length;
  
  if (imageCount === 1) {
    return (
      <div className="w-full max-w-sm">
        <img 
          src={images[0]} 
          alt="Shared image"
          className="w-full h-auto rounded-lg cursor-pointer hover:opacity-90"
          onClick={() => onImageClick(0)}
        />
      </div>
    );
  }

  if (imageCount === 2) {
    return (
      <div className="grid grid-cols-2 gap-1 w-full max-w-sm">
        {images.map((img, index) => (
          <img 
            key={index}
            src={img} 
            alt={`Image ${index + 1}`}
            className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-90"
            onClick={() => onImageClick(index)}
          />
        ))}
      </div>
    );
  }

  if (imageCount === 3) {
    return (
      <div className="grid grid-cols-2 gap-1 w-full max-w-sm">
        <img 
          src={images[0]} 
          alt="Image 1"
          className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-90"
          onClick={() => onImageClick(0)}
        />
        <div className="grid grid-rows-2 gap-1">
          <img 
            src={images[1]} 
            alt="Image 2"
            className="w-full h-15 object-cover rounded cursor-pointer hover:opacity-90"
            onClick={() => onImageClick(1)}
          />
          <img 
            src={images[2]} 
            alt="Image 3"
            className="w-full h-15 object-cover rounded cursor-pointer hover:opacity-90"
            onClick={() => onImageClick(2)}
          />
        </div>
      </div>
    );
  }

  // 4+ images
  return (
    <div className="grid grid-cols-2 gap-1 w-full max-w-sm">
      {images.slice(0, 3).map((img, index) => (
        <img 
          key={index}
          src={img} 
          alt={`Image ${index + 1}`}
          className="w-full h-24 object-cover rounded cursor-pointer hover:opacity-90"
          onClick={() => onImageClick(index)}
        />
      ))}
      <div 
        className="relative w-full h-24 bg-gray-200 rounded cursor-pointer hover:opacity-90 flex items-center justify-center"
        onClick={() => onImageClick(3)}
      >
        <img 
          src={images[3]} 
          alt="Image 4"
          className="w-full h-full object-cover rounded opacity-50"
        />
        {imageCount > 4 && (
          <div className="absolute inset-0 bg-black bg-opacity-50 rounded flex items-center justify-center">
            <span className="text-white text-lg font-bold">+{imageCount - 3}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const ImageViewer = ({ images, initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="relative w-full h-full flex items-center justify-center">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
        >
          <X size={32} />
        </button>
        
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 text-white hover:text-gray-300 z-10"
            >
              <ChevronDown size={32} className="rotate-90" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 text-white hover:text-gray-300 z-10"
            >
              <ChevronDown size={32} className="-rotate-90" />
            </button>
          </>
        )}

        <img 
          src={images[currentIndex]} 
          alt={`Image ${currentIndex + 1}`}
          className="max-w-full max-h-full object-contain"
        />

        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-3 py-1 rounded">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
};

const UserProfileDetail = ({ 
  chat,
  onClose
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setAlert = useAlert();
  const { user: currentUser } = useAuthStore();

  const [participantsExpanded, setParticipantsExpanded] = useState(true);

  const [showAddMember, setShowAddMember] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super-admin';

  const { data: allUsersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['users', searchQuery],
    queryFn: async () => {
      const response = await AxiosInstance.get(`/users?limit=10${searchQuery ? `&search=${searchQuery}` : ''}`);
      return response.data;
    },
    enabled: showAddMember && isAdmin
  });

  const createChatMutation = useMutation({
    mutationFn: createDirectChat,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['userChats']);
      const chat = data.chat;
      navigate(`/clients/messages/${chat._id || chat.id}`);
      onClose();
    },
    onError: () => {
      setAlert({ type: 'error', message: 'Failed to start direct chat' });
    }
  });

  const addMemberMutation = useMutation({
    mutationFn: (userId) => addParticipantToChat(chat._id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['chat', chat._id]);
      setShowAddMember(false);
      setSearchQuery('');
      setAlert({ type: 'success', message: 'Member added to chat' });
    },
    onError: (error) => {
      setAlert({ type: 'error', message: error.response?.data?.error || 'Failed to add member' });
    }
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId) => removeParticipantFromChat(chat._id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['chat', chat._id]);
      setAlert({ type: 'success', message: 'Member removed from chat' });
    },
    onError: (error) => {
      setAlert({ type: 'error', message: error.response?.data?.error || 'Failed to remove member' });
    }
  });

  if (!chat) return null;

  const toggleParticipants = () => setParticipantsExpanded(!participantsExpanded);

  // Determine display info
  const isOrderChat = chat.chatType === 'order';
  let title = '';
  let avatar = tm1;
  let subTitle = '';

  if (isOrderChat) {
    title = chat.orderId?.title || chat.orderId?.gigId?.title || 'Order Chat';
    title = title.length > 35 ? title.substring(0, 35) + '...' : title;
    avatar = chat.orderId?.img || chat.orderId?.gigId?.cover || tm1;
    subTitle = chat.orderId ? `Order #${chat.orderId._id.substring(chat.orderId._id.length - 8).toUpperCase()}` : '';
  } else {
    const currentUserId = currentUser?._id || currentUser?.id;
    const other = chat.participants.find(p => p._id?.toString() !== currentUserId?.toString());
    title = other ? `${other.firstName} ${other.lastName}` : 'Direct Chat';
    avatar = other?.profilePicture || userimg;
    subTitle = other?.username || '';
  }

  const handleMessageParticipant = (participant) => {
    createChatMutation.mutate(participant._id);
  };

  return (
    <div className="h-full absolute md:static left-0 flex flex-col bg-white dark:bg-[#141414] font-['Poppins'] overflow-y-auto border-l dark:border-gray-800 z-50 w-full">
      {/* Header with close button */}
      <div className="flex justify-end p-4">
        <button onClick={onClose} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border-[1px] border-[#E3E8E7] dark:border-[#32353c] rounded-full p-1">
          <X size={20} />
        </button>
      </div>
      
      {/* Profile / Chat Info */}
      <div className="flex flex-col items-center px-4 pb-6">
        <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-2 border-gray-100">
          <img 
            src={avatar?.images?.[0] || avatar} 
            alt={title} 
            className="w-full h-full object-cover"
          />
        </div>
        <h2 className="text-[15px] font-medium text-center dark:text-white">
          {title.length > 20 ? title.substring(0, 20) : title}
        </h2>
        
        <p className="text-gray-500 dark:text-gray-400 text-[12px] font-normal">{subTitle}</p>
      </div>
      
  
      {/* Participants List */}
      <div className="border-b dark:border-gray-800">
        <div className="px-6 py-4 flex justify-between items-center cursor-pointer" onClick={toggleParticipants}>
          <div className="flex items-center">
            <span className="font-medium dark:text-white">Participants</span>
            <span className="text-gray-500 dark:text-gray-400 text-sm ml-2">({chat.participants.length})</span>
          </div>
          {participantsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        {participantsExpanded && (
          <div className="px-6 pb-4 space-y-4">
            {chat.participants.map(p => {
              const currentUserId = currentUser?._id || currentUser?.id;
              const isMe = p._id?.toString() === currentUserId?.toString();
              return (
              <div key={p._id} className="flex items-center justify-between group">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full overflow-hidden mr-3 bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                    <img 
                      src={p.profilePicture || tm1} 
                      alt={p.firstName} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate w-32 dark:text-white">
                      {p.firstName} {p.lastName} {isMe && '(You)'}
                    </p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 capitalize">{p.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isAdmin && isOrderChat && !isMe && (
                    <button 
                      onClick={() => removeMemberMutation.mutate(p._id)}
                      disabled={removeMemberMutation.isPending}
                      className="p-1.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove member"
                    >
                      <UserMinus size={16} />
                    </button>
                  )}
                  {!isMe && p.directMessages && chat.chatType !== 'direct' && (
                    <button 
                      onClick={() => handleMessageParticipant(p)}
                      className="p-1.5 text-[#7295B8] hover:bg-blue-50 rounded-full transition-colors"
                      title="Send Direct Message"
                    >
                      <MessageSquare size={16} />
                    </button>
                  )}
                </div>
              </div>
            )})}

            {isAdmin && isOrderChat && (
              <div className="pt-2">
                {!showAddMember ? (
                  <button 
                    onClick={() => setShowAddMember(true)}
                    className="w-full py-2 border-2 border-dashed border-gray-100 text-gray-400 rounded-lg text-xs flex items-center justify-center hover:border-blue-200 hover:text-blue-500 transition-all font-medium"
                  >
                    <UserPlus size={14} className="mr-2" />
                    Add Team Member
                  </button>
                ) : (
                  <div className="space-y-2 bg-gray-50 dark:bg-[#1d2026] p-2 rounded-lg border border-gray-100 dark:border-[#32353c]">
                    <div className="flex items-center bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-md px-2 py-1">
                      <Search size={12} className="text-gray-400 mr-2" />
                      <input 
                        type="text" 
                        placeholder="Search users..."
                        className="bg-transparent border-none text-[11px] w-full focus:ring-0 p-0"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                      />
                      <button onClick={() => {setShowAddMember(false); setSearchQuery('');}} className="p-0.5">
                        <X size={12} className="text-gray-400" />
                      </button>
                    </div>
                    
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {isLoadingUsers ? (
                        <div className="flex justify-center p-2"><Loader2 className="animate-spin text-gray-400" size={12} /></div>
                      ) : allUsersData?.users?.filter(u => !chat.participants.some(p => p._id === u._id)).length > 0 ? (
                        allUsersData?.users
                          ?.filter(u => !chat.participants.some(p => p._id === u._id))
                          .map(u => (
                            <button 
                              key={u._id}
                              onClick={() => addMemberMutation.mutate(u._id)}
                              disabled={addMemberMutation.isPending}
                              className="w-full text-left p-1.5 hover:bg-blue-50 rounded flex items-center justify-between transition-colors"
                            >
                              <div className="flex items-center">
                                <div className="w-6 h-6 rounded-full bg-gray-200 mr-2 overflow-hidden flex-shrink-0">
                                  {u.profilePicture && <img src={u.profilePicture} alt="" className="w-full h-full object-cover" />}
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[10px] font-medium leading-tight">{u.firstName} {u.lastName}</span>
                                  <span className="text-[8px] text-gray-500 capitalize">{u.role}</span>
                                </div>
                              </div>
                              <UserPlus size={12} className="text-blue-500" />
                            </button>
                          ))
                      ) : searchQuery ? (
                        <p className="text-[9px] text-center text-gray-400 py-2">No users found</p>
                      ) : (
                        <p className="text-[9px] text-center text-gray-400 py-2">Type to search...</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      
  

    </div>
  );
};

const ChatConversation = ({ 
  onClose,
  isMobile = false
}) => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setAlert = useAlert();
  const { user } = useAuthStore();
  
  const [message, setMessage] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [fileType, setFileType] = useState('image');
  const [previewMessage, setPreviewMessage] = useState('');
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [isInitiatingCall, setIsInitiatingCall] = useState(false);
  
  const messagesEndRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Fetch Chat Details
  const { data: chatDetails, isLoading: isLoadingChat } = useQuery({
    queryKey: ['chat', chatId],
    queryFn: () => getChatById(chatId),
    enabled: !!chatId
  });

  // Fetch Messages
  const { data: messagesData, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['messages', chatId],
    queryFn: () => getChatMessages(chatId),
    enabled: !!chatId,
    // Polling removed in favor of WebSockets
  });

  const messages = messagesData?.messages || [];

  // Initialize online users from chat details
  useEffect(() => {
    if (chatDetails?.chat?.participants) {
      const online = new Set(
        chatDetails.chat.participants
          .filter(p => p.isOnline)
          .map(p => p._id.toString())
      );
      setOnlineUsers(online);
    }
  }, [chatDetails]);

  // Socket setup
  useEffect(() => {
    if (!chatId) return;

    // Connect and join room
    SocketService.connect();
    SocketService.joinChat(chatId);

    // Listen for new messages
    const handleNewMessage = (newMessage) => {
      // Refresh messages query when a new message arrives in THIS chat
      if (newMessage.chatId === chatId) {
        queryClient.invalidateQueries(['messages', chatId]);
        queryClient.invalidateQueries(['userChats']);
        markReadMutation.mutate();
      }
    };

    SocketService.onNewMessage(handleNewMessage);

    // Online status listeners
    const handleUserOnline = ({ userId }) => {
      setOnlineUsers(prev => new Set([...prev, userId.toString()]));
    };

    const handleUserOffline = ({ userId }) => {
      setOnlineUsers(prev => {
        const next = new Set(prev);
        next.delete(userId.toString());
        return next;
      });
    };

    SocketService.socket?.on('user:online', handleUserOnline);
    SocketService.socket?.on('user:offline', handleUserOffline);

    return () => {
      SocketService.leaveChat(chatId);
      SocketService.offNewMessage();
      SocketService.socket?.off('user:online', handleUserOnline);
      SocketService.socket?.off('user:offline', handleUserOffline);
    };
  }, [chatId, queryClient]);

  // Send Message Mutation
  const sendMutation = useMutation({
    mutationFn: sendMessage,
    onSuccess: () => {
      queryClient.invalidateQueries(['messages', chatId]);
      queryClient.invalidateQueries(['userChats']);
    }
  });

  // Mark Read Mutation
  const markReadMutation = useMutation({
    mutationFn: () => markMessagesAsRead(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries(['unreadCount']);
    }
  });

  useEffect(() => {
    if (chatId) {
      markReadMutation.mutate();
    }
  }, [chatId, messages.length]);

  // Derived contact info
  const contact = React.useMemo(() => {
    const chat = chatDetails?.chat;
    if (!chat) return null;
    
    if (chat.chatType === 'order') {
      return {
        name: chat.orderId?.title || chat.orderId?.gigId?.title || 'Order Chat',
        avatar: chat.orderId?.img || chat.orderId?.gigId?.cover || tm1,
        status: 'Order Specific Chat',
        isOnline: true,
        chatType: 'order'
      };
    } else {
      const currentUserId = user?._id || user?.id;
      const other = chat.participants.find(p => p._id?.toString() !== currentUserId?.toString());
      const otherId = other?._id?.toString();
      const isOnline = otherId ? onlineUsers.has(otherId) : false;

      return {
        name: other ? `${other.firstName} ${other.lastName}` : 'Direct Chat',
        avatar: other?.profilePicture || userimg,
        username: other?.username || '',
        status: isOnline ? 'Online' : 'Offline',
        isOnline: isOnline,
        chatType: 'direct'
      };
    }
  }, [chatDetails, user, onlineUsers]);

  // Map messages to UI format
  const mappedMessages = React.useMemo(() => {
    if (!messagesData?.messages) return pendingMessages;
    
    const currentUserId = user?._id || user?.id;
    // Map existing messages
    const apiMessages = messagesData.messages.map(msg => ({
      id: msg._id,
      sender: (msg.senderId._id?.toString() === currentUserId?.toString() || msg.senderId?.toString() === currentUserId?.toString()) ? 'user' : 'contact',
      text: msg.content,
      time: new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      timestamp: new Date(msg.createdAt),
      read: (msg.isRead?.length || 0) > 1,
      type: msg.attachments?.length > 0 
        ? (msg.attachments[0].type === 'image' ? 'images' : (msg.attachments[0].type === 'audio' ? 'audio' : 'documents')) 
        : 'text',
      images: msg.attachments?.filter(a => a.type === 'image').map(a => a.url) || [],
      documents: msg.attachments?.filter(a => a.type === 'document').map(a => ({ name: a.name || a.url?.split('/').pop() || 'File', size: a.size || 'N/A', url: a.url })) || [],
      audio: msg.attachments?.filter(a => a.type === 'audio').map(a => ({ name: a.name || 'Voice Note', size: a.size || 'N/A', url: a.url })) || [],
      isPending: false,
      senderData: msg.senderId
    }));

    // Filter out pending messages that already exist in the API response
    // We compare by text content and a small time window to avoid duplicates
    const filteredPendingMessages = pendingMessages.filter(pending => {
      const isAlreadyInApi = apiMessages.some(api => 
        api.text === pending.text && 
        api.sender === pending.sender &&
        Math.abs(new Date(api.timestamp) - new Date(pending.timestamp)) < 10000 // 10 second window
      );
      return !isAlreadyInApi;
    });

    return [...apiMessages, ...filteredPendingMessages];
  }, [messagesData, pendingMessages, user]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mappedMessages.length]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle paste events
  useEffect(() => {
    const handlePaste = (e) => {
      const items = Array.from(e.clipboardData.items);
      const images = items.filter(item => item.type.startsWith('image/'));
      
      if (images.length > 0) {
        e.preventDefault();
        const files = images.map(item => {
          const file = item.getAsFile();
          return {
            file,
            preview: URL.createObjectURL(file),
            name: file.name || 'pasted-image.png',
            size: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
            type: file.type
          };
        });
        
        setSelectedFiles(files);
        setFileType('image');
        setPreviewMessage('');
        setShowFilePreview(true);
      } else {
        // Handle text paste
        const text = e.clipboardData.getData('text');
        if (text) {
          setMessage(prev => prev + text);
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const handleCall = async (type) => {
    try {
      setIsInitiatingCall(true);
      // roomID should be the chatId for simplicity in group/direct calls
      const roomID = chatId;
      
      // Call backend to send email invitations
      await initiateCall(chatId, {
        callType: type,
        roomID: roomID
      });

      // Navigate to call page
      navigate(`/call/${roomID}?type=${type}`);
    } catch (error) {
      console.error('Failed to initiate call:', error);
      alert('Failed to initiate call. Please try again.');
    } finally {
      setIsInitiatingCall(false);
    }
  };
  
  const handleSend = async () => {
    const currentText = message;
    setMessage('');
    
    const tempId = Date.now().toString();
    const optimisticMsg = {
      id: tempId,
      sender: 'user',
      text: currentText,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      timestamp: new Date(),
      type: 'text',
      isPending: true
    };
    
    setPendingMessages(prev => [...prev, optimisticMsg]);

    try {
      await sendMutation.mutateAsync({
        chatId,
        content: currentText
      });
      // We don't remove from pendingMessages here!
      // The mappedMessages memo now handles the handoff smoothly.
      // But we should clean up OLD pending messages eventually.
      setTimeout(() => {
        setPendingMessages(prev => prev.filter(m => m.id !== tempId));
      }, 3000); 
    } catch (err) {
      console.error('Send message error:', err);
      setPendingMessages(prev => prev.filter(m => m.id !== tempId));
      setMessage(currentText); // Restore on error
    }
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const toggleProfile = () => {
    setShowProfile(!showProfile);
  };

  const handleEmojiSelect = (emoji) => {
    setMessage(prevMessage => prevMessage + emoji);
    setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const handleImageSelect = () => {
    imageInputRef.current?.click();
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const imageFiles = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
      type: file.type
    }));

    setSelectedFiles(imageFiles);
    setFileType('image');
    setPreviewMessage('');
    setShowFilePreview(true);
    e.target.value = '';
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const documentFiles = files.map(file => ({
      file,
      name: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
      type: file.type
    }));

    setSelectedFiles(documentFiles);
    setFileType('document');
    setPreviewMessage('');
    setShowFilePreview(true);
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    
    if (newFiles.length === 0) {
      setShowFilePreview(false);
    }
  };

  const handleSendFiles = async (files, caption) => {
    setIsUploading(true);
    const attachments = [];
    
    const tempId = Date.now().toString();
    try {
      const optimisticMsg = {
        id: tempId,
        sender: 'user',
        text: caption,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        timestamp: new Date(),
        type: fileType === 'image' ? 'images' : 'documents',
        images: fileType === 'image' ? files.map(f => f.preview) : [],
        documents: fileType !== 'image' ? files.map(f => ({ name: f.name, size: f.size, url: '#' })) : [],
        isPending: true
      };
      
      setPendingMessages(prev => [...prev, optimisticMsg]);

      for (const fileObj of files) {
        const data = new FormData();
        data.append('file', fileObj.file);
        data.append('upload_preset', process.env.CLOUDINARY_UPLOAD_PRESET || 'foogles');
        
        const cloudName = process.env.CLOUDINARY_CLOUD_NAME || 'djstk2ukm';
        const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`, {
          method: 'POST',
          body: data
        });
        
        const cloudData = await res.json();
        if (cloudData.secure_url) {
          attachments.push({
            url: cloudData.secure_url,
            name: fileObj.name,
            type: fileType === 'image' ? 'image' : 'document',
            size: fileObj.size
          });
        }
      }

      await sendMutation.mutateAsync({
        chatId,
        content: caption,
        attachments
      });
      
      // We don't remove from pendingMessages here!
      // The mappedMessages memo handles the deduplication.
      // Cleanup after a longer period to ensure handoff.
      setTimeout(() => {
        setPendingMessages(prev => prev.filter(m => m.id !== tempId));
      }, 5000);
      setSelectedFiles([]);
      setPreviewMessage('');
    } catch (err) {
      console.error('Upload/Send error:', err);
      // Remove the optimistic message on error so it doesn't look like it's stuck sending
      setPendingMessages(prev => prev.filter(m => m.id !== tempId)); 
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageClick = (images, index) => {
    setViewerImages(images);
    setViewerIndex(index);
    setShowImageViewer(true);
  };

  const handlePasteAction = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setMessage(prev => prev + text);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      console.log('Clipboard access not available');
    }
  };

  const handleMessageAction = (messageId) => {
    setActiveDropdown(activeDropdown === messageId ? null : messageId);
  };

  const handleDeleteMessage = (messageId) => {
    // Backend API for deletion not yet implemented
    // setMessages(messages.filter(msg => msg.id !== messageId));
    setActiveDropdown(null);
  };



  const handleSaveEdit = () => {
    // Backend API for editing not yet implemented
    /* 
    setMessages(messages.map(msg => 
      msg.id === editingMessage 
        ? { ...msg, text: editText, edited: true }
        : msg
    )); 
    */
    setEditingMessage(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditText('');
  };

  return (
    <div className="flex h-full w-full bg-gray-50 dark:bg-[#0A0A0A] font-['Poppins']">
      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleImageChange}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Main Chat Area */}
      <div className={`flex flex-col flex-1 h-full overflow-hidden bg-gray-50 dark:bg-[#0A0A0A] transition-all duration-300 ${showProfile ? 'w-2/3' : 'w-full'}`}>
        {/* Header */}
        <div className="bg-white dark:bg-[#141414] p-4 border-b dark:border-gray-800 flex items-center justify-between flex-shrink-0 z-10 relative">
          <div className="flex items-center">
            <div className="relative">
              <img 
                src={contact?.avatar || tm1} 
                alt={contact?.name} 
                className="w-10 h-10 rounded-full object-cover cursor-pointer"
                onClick={toggleProfile}
              />
              {contact?.isOnline && (
                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white"></div>
              )}
            </div>
            <div className="ml-3">
              <h2 className="font-medium text-[#171C1B] dark:text-white">
                {contact?.name?.length > 20 ? contact.name.substring(0, 20) + '...' : contact?.name || '...'}
              </h2>
              <div className="flex items-center gap-1">
                <p className="text-xs text-[#465352] dark:text-gray-400">{contact?.status}</p>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
              <button 
                onClick={() => handleCall('video')}
                className="p-2 rounded-full text-[#ACB9B8] border-[1px] border-[#E3E8E7] dark:border-[#32353c] hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <img src={video} alt='video call'/>
              </button>
            <button 
              onClick={toggleProfile}
              className={`px-2 py-2 rounded-full text-[#ACB9B8] border-[1px] border-[#E3E8E7] dark:border-[#32353c] hover:bg-gray-100 dark:hover:bg-[#32353c] transition-colors ${
                showProfile ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-100' : ''
              }`}
            >
              <MoreHorizontal size={22} />
            </button> 
          </div>

        </div>
        
        {/* Messages container */}
        <div className="relative flex-1 bg-[#F5F5F5] dark:bg-[#0A0A0A] overflow-hidden flex flex-col">
          {/* Mobile Floating Back Button */}
          {isMobile && onClose && (
            <button 
              onClick={onClose}
              className="md:hidden absolute top-4 left-4 z-[40] bg-white dark:bg-[#1d2026] p-2.5 rounded-full shadow-lg border border-gray-200 dark:border-gray-800 text-[#174568] dark:text-gray-300 hover:text-[#7295B8] active:scale-95 transition-all outline-none"
              title="Back to Messages"
            >
              <ArrowLeft size={18} />
            </button>
          )}

          {/* Mobile Floating Refresh Button */}
          <button 
            onClick={() => {
              queryClient.invalidateQueries(['messages', chatId]);
              queryClient.invalidateQueries(['userChats']);
            }}
            className="md:hidden absolute top-4 right-4 z-[40] bg-white dark:bg-[#1d2026] p-2.5 rounded-full shadow-lg border border-gray-200 dark:border-gray-800 text-[#174568] dark:text-gray-300 hover:text-[#7295B8] active:scale-95 transition-all outline-none"
            title="Refresh Chat"
          >
            <RefreshCw size={18} className={isLoadingMessages ? "animate-spin text-[#7295B8]" : ""} />
          </button>
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4">
          {isLoadingMessages ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="animate-spin text-[#7295B8]" size={32} />
            </div>
          ) : mappedMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
               <MessageSquare size={48} className="mb-2 opacity-20" />
               <p>No messages yet. Start the conversation!</p>
            </div>
          ) : mappedMessages.map((msg) => (
            <div 
              key={msg.id} 
              className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'} group`}
            >
              {msg.sender === 'contact' && (
                <img 
                  src={msg.senderData?.profilePicture || contact?.avatar || tm2} 
                  alt={contact?.name} 
                  className="w-6 h-6 rounded-full mr-2 self-end object-cover"
                />
              )}
              <div className="max-w-[70%] relative flex items-end">
                <div 
                  className={`p-2 rounded-tl-[20px] rounded-tr-[20px] text-[13px] relative ${
                    msg.sender === 'user' 
                      ? 'bg-[#7295B8] text-white rounded-bl-[20px] mr-2' 
                      : 'bg-white dark:bg-[#1d2026] text-gray-800 dark:text-gray-200 rounded-br-[20px]'
                  }`}
                >
                 
                 

                  {/* Images */}
                  {msg.type === 'images' && (
                    <div className="mb-2">
                      <ImageCollage 
                        images={msg.images} 
                        onImageClick={(index) => handleImageClick(msg.images, index)}
                      />
                    </div>
                  )}
                  
                  {/* Documents */}
                  {msg.type === 'documents' && (
                    <div className="mb-2 space-y-2">
                      {msg.documents.map((doc, index) => (
                        <a 
                          key={index} 
                          href={doc.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-1 bg-gray-100 dark:bg-[#1d2026] rounded-lg hover:bg-gray-200 dark:hover:bg-[#1d2026] transition-colors w-full decoration-none mb-2 last:mb-0"
                        >
                          <File size={24} className="text-gray-600 dark:text-gray-200 mr-3" />
                          <div className="flex-1 overflow-hidden">
                            <p className="font-medium text-[10px] text-gray-800 dark:text-gray-200 truncate">
                              {doc.name.length > 10 ? doc.name.substring(0, 10) + '...' : doc.name}
                            </p>
                            <p className="text-[9px] text-gray-500">{doc.size}</p>
                          </div>
                          <div className="text-blue-600 ml-2">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}


                  {/* Text content - Edit mode or display mode */}
                  {msg.text && (
                    <>
                      {editingMessage === msg.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="min-w-[20rem] min-h-[20rem] p-2 text-sm border-none focus:ring-0 rounded resize-none text-gray-800"
                            rows="2"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={handleSaveEdit}
                              className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-[12px] whitespace-pre-wrap break-all max-w-[43ch]">{msg.text}</p>
                          {msg.edited && (
                            <p className="text-xs opacity-70 mt-1">edited</p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className={`text-xs mt-1 flex gap-1 items-center ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.isPending && (
                    <span className="text-gray-400 italic">Sending...</span>
                  )}

                  <div className='flex flex-col gap-1'>
                  <span className="text-gray-400 text-[11px]">{msg.time}</span>
                  {msg.sender === 'user' && !msg.isPending && (
                    <span className="ml-1">
                      {msg.read ? (
                        <div className="text-blue-500">
                          <img src={check}/>
                        </div>
                      ) : (
                        <div className="text-gray-400">
                          <Check size={14} />
                        </div>
                      )}
                    </span>
                  )}
                  </div>
                </div>
              </div>
              {msg.sender === 'user' && (
                <img 
                  src={user?.profilePicture || userimg}
                  alt="You" 
                  className="w-8 h-8 rounded-full ml-2 self-end object-cover"
                />
              )}
            </div>
          ))}
          
          <div ref={messagesEndRef} />
          </div>
        </div>
        
        {/* Message input */}
        <div className="bg-[#F5F5F5] dark:bg-[#1d2026] p-3 relative w-full flex-shrink-0 z-10 border-t border-transparent dark:border-black">
          <div className="flex flex-col items-center w-full max-w-[95%] bg-white dark:bg-[#141414] border-[1px] border-[#E3E8E7] dark:border-[#32353c] mx-auto rounded-[20px] p-1 overflow-hidden">
                 <textarea
              
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type a message..."
              className="w-full flex-1 text-[12px] font-normal p-2 border-0 focus:ring-0 focus:outline-none bg-transparent dark:text-white dark:placeholder-gray-500"
            ></textarea>
             <div className="w-full flex items-center justify-between border-t dark:border-[#32353c]">
            <div className="flex space-x-2 mr-2">
           

              <button 
                type="button" 
                onClick={handleImageSelect}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors border-r dark:border-[#32353c]"
                title="Send Images"
              >
                <Image size={20} />
              </button>

              <div className="relative" ref={emojiPickerRef}>
                <button 
                  type="button" 
                  onClick={toggleEmojiPicker}
                  className={`p-2 text-gray-500 hover:text-gray-700 transition-colors ${
                    showEmojiPicker ? 'text-blue-600' : ''
                  }`}
                  title="Emojis"
                >
                  <Smile size={20} />
                </button>
                <EmojiPicker 
                  isOpen={showEmojiPicker}
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                />
              </div>
              <button 
                type="button" 
                onClick={handleFileSelect}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors border-r dark:border-[#32353c]"
                title="Send Documents, Images, Videos"
              >
                <Paperclip size={20} />
              </button>
              <button 
                type="button" 
                onClick={handlePasteAction}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                title="Paste from Clipboard"
              >
                <ClipboardPaste size={20} />
              </button>
            </div>
       
            <div className="flex space-x-2 ml-2">
           
              <button 
                onClick={handleSend}
                className={`p-2 rounded-full transition-colors rotate-[45deg] ${
                  message.trim() ? 'text-[#7295B8] hover:bg-blue-50' : 'text-gray-400'
                }`}
                disabled={!message.trim()}
              >
                <BsSendFill size={23} />
              </button>
            </div>
            </div>

          </div>
        </div>
      </div>
      {/* Profile Panel */}
      {showProfile && (
        <div className="w-1/3 min-w-[300px]">
          <UserProfileDetail 
            chat={chatDetails?.chat}
            onClose={toggleProfile}
          />
        </div>
      )}


      {/* File Preview Modal */}
      {showFilePreview && (
        <FilePreviewModal
          files={selectedFiles}
          onClose={() => setShowFilePreview(false)}
          onRemoveFile={handleRemoveFile}
          onSend={handleSendFiles}
          message={previewMessage}
          setMessage={setPreviewMessage}
          type={fileType}
        />
      )}

      {/* Image Viewer Modal */}
      {showImageViewer && (
        <ImageViewer
          images={viewerImages}
          initialIndex={viewerIndex}
          onClose={() => setShowImageViewer(false)}
        />
      )}

      {/* Call Initiating Modal */}
      {isInitiatingCall && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex flex-col items-center justify-center z-[100]">
          <div className="bg-white dark:bg-[#1d2026] p-8 rounded-2xl shadow-2xl flex flex-col items-center">
            <Loader2 className="animate-spin text-[#7295B8] mb-4" size={50} />
            <h3 className="text-[15px] md:text-xl font-medium text-gray-800 dark:text-white">Connecting...</h3>
            <p className="text-[12px] md:text-sm text-gray-500 dark:text-gray-400 mt-2 text-center max-w-xs">Just a second, bringing everyone together.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatConversation;